<script setup lang="ts">
import { PhCrosshair, PhWarningCircle } from '@phosphor-icons/vue'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Spinner } from '@/components/ui/spinner'

defineProps<{
  isLocating: boolean
  error: string
}>()

defineEmits<{
  locate: []
}>()
</script>

<template>
  <div class="flex flex-col items-end gap-2">
    <!--
      Icon-only, and sized to match the basemap toggle beside it. The previous
      text button ("My location") was wide enough to overhang the map's right
      edge at 390px and collided with the attribution bar underneath.
      Square, not round: DESIGN.md is rectangular by default and reserves
      `rounded-full` for genuinely circular elements.
    -->
    <button
      type="button"
      class="pointer-events-auto flex size-11 items-center justify-center bg-background text-foreground ring-1 ring-foreground/25 transition-colors hover:bg-muted disabled:opacity-60"
      :aria-label="isLocating ? 'Finding your location' : 'Show my location'"
      :disabled="isLocating"
      @click="$emit('locate')"
    >
      <Spinner v-if="isLocating" class="size-5" />
      <!-- Crosshair-with-dot, matching the reference app's locate affordance.
           Primary-coloured because it is an action, and blue is the system's
           single action colour — the shape stays rectangular per DESIGN.md
           rather than copying the reference app's round FAB. -->
      <PhCrosshair v-else class="size-5 text-primary" />
    </button>

    <Alert
      v-if="error"
      variant="destructive"
      class="pointer-events-auto max-w-56 ring-1 ring-foreground/10"
    >
      <PhWarningCircle />
      <AlertTitle>Location unavailable</AlertTitle>
      <AlertDescription>{{ error }}</AlertDescription>
    </Alert>
  </div>
</template>
